import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// helpers
import {
  getProducts as getProductList,
  createProductApi,
  getProduct as getProductApi,
  updateProductApi,
  deleteProductApi,
} from "../../helpers/";

// constants
import { ProductsActionTypes } from "./constants";
import {
  createProductError,
  createProductSuccess,
  productsGetListError,
  productsGetListSuccess,
  getProductSuccess,
  getProductError,
  updateProductSuccess,
  updateProductError,
  deleteProductSuccess,
  deleteProductError,
} from "./actions";

interface ProductData {
  payload: {
    limit: number;
    skip: number;
    keyword?: string;
    owner?: string;
    country?: string;
    mode?: string;
    startDate?: string;
    endDate?: string;
  };
  type: string;
}

interface ProductCreateData {
  payload: {
    country: string;
    affiliate: string;
    name: string;
    price: number;
    description: string;
    quantity: number;
    comment: string;
    productImage: string;
  };
  type: string;
}

interface ProductUpdateData {
  payload: {
    id: string;
    name: string;
    price: number;
    description: string;
    quantity: number;
    comment: string;
    productImage: string;
  };
  type: string;
}

interface ProductDeleteData {
  payload: {
    ids: string[];
  };
  type: string;
}

interface GetProductData {
  payload: {
    id: string;
  };
  type: string;
}

function* getProducts({
  payload: { limit, skip, keyword, owner, country, mode, startDate, endDate },
}: ProductData): SagaIterator {
  try {
    const params: any = { limit, skip, keyword, owner, country, mode };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = yield call(getProductList, params);
    // Voir orders/saga : on ignore le 403 avalé et le data null pour ne pas
    // écraser la liste courante.
    if ((response as any)?.__handled403) return;
    const products = response.data;
    if (products == null) return;
    yield put(
      productsGetListSuccess(ProductsActionTypes.GET_LIST_PRODUCTS, products)
    );
  } catch (error: any) {
    yield put(
      productsGetListError(ProductsActionTypes.GET_LIST_PRODUCTS, error)
    );
  }
}

function* createProduct({
  payload: {
    country,
    affiliate,
    name,
    price,
    description,
    quantity,
    comment,
    productImage,
  },
}: ProductCreateData): SagaIterator {
  try {
    const response = yield call(createProductApi, {
      country,
      affiliate,
      name,
      price,
      description,
      quantity,
      comment,
      productImage,
    });
    const data = response.data;
    const product = data?.product ?? data;
    const created = data?.created !== false;
    yield put(
      createProductSuccess(ProductsActionTypes.CREATE_PRODUCT, {
        product,
        created
      })
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      createProductError(ProductsActionTypes.CREATE_PRODUCT, errorMessage)
    );
  }
}

function* updateProduct({
  payload: { id, name, price, description, quantity, comment, productImage },
}: ProductUpdateData): SagaIterator {
  try {
    const response = yield call(updateProductApi, {
      id,
      name,
      price,
      description,
      quantity,
      comment,
      productImage,
    });
    const product = response.data;
    yield put(
      updateProductSuccess(ProductsActionTypes.UPDATE_PRODUCT, product)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      updateProductError(ProductsActionTypes.UPDATE_PRODUCT, errorMessage)
    );
  }
}

function* deleteProduct({ payload: { ids } }: ProductDeleteData): SagaIterator {
  try {
    const response = yield call(deleteProductApi, {
      ids,
    });
    const product = response.data;
    yield put(
      deleteProductSuccess(ProductsActionTypes.DELETE_PRODUCT, product)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      deleteProductError(ProductsActionTypes.DELETE_PRODUCT, errorMessage)
    );
  }
}

function* getProduct({ payload: { id } }: GetProductData): SagaIterator {
  try {
    const response = yield call(getProductApi, { id });
    const product = response.data;
    yield put(getProductSuccess(ProductsActionTypes.GET_PRODUCT, product));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(getProductError(ProductsActionTypes.GET_PRODUCT, errorMessage));
  }
}

export function* watchGetProducts() {
  yield takeEvery(ProductsActionTypes.GET_LIST_PRODUCTS, getProducts);
}

export function* watchCreateProducts() {
  yield takeEvery(ProductsActionTypes.CREATE_PRODUCT, createProduct);
}

export function* watchUpdateProducts() {
  yield takeEvery(ProductsActionTypes.UPDATE_PRODUCT, updateProduct);
}

export function* watchDeleteProducts() {
  yield takeEvery(ProductsActionTypes.DELETE_PRODUCT, deleteProduct);
}

export function* watchGetProduct() {
  yield takeEvery(ProductsActionTypes.GET_PRODUCT, getProduct);
}

function* productsSaga() {
  yield all([
    fork(watchGetProducts),
    fork(watchCreateProducts),
    fork(watchGetProduct),
    fork(watchUpdateProducts),
    fork(watchDeleteProducts),
  ]);
}

export default productsSaga;
